import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Lock, ArrowLeft, CheckCircle2, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasswortZurueckSetzen = () => {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError(t('pw_reset.errors.token_missing'));
      return;
    }
    if (pw.length < 8) {
      setError(t('pw_reset.errors.password_too_short'));
      return;
    }
    if (pw !== pw2) {
      setError(t('pw_reset.errors.passwords_mismatch'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, { token, new_password: pw, sprache: (i18n?.language || 'de').slice(0, 2) });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || t('pw_reset.errors.reset_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfcfd] flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md bg-white border-t-4 border-[#b8893d] shadow-sm p-8" data-testid="reset-pw-card">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-[#34536b] hover:text-[#1B3A5C] mb-6">
            <ArrowLeft className="w-3 h-3" /> {t('pw_reset.back_to_login')}
          </Link>
          {done ? (
            <div className="text-center space-y-4" data-testid="reset-pw-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              <h1 className="font-serif text-2xl text-[#1B3A5C]">{t('pw_reset.success.title')}</h1>
              <p className="text-sm text-[#34536b]">
                {t('pw_reset.success.description')}
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-2xl text-[#1B3A5C] mb-2">{t('pw_reset.form.title')}</h1>
              <p className="text-sm text-[#34536b] mb-6">
                {t('pw_reset.form.description')}
              </p>
              {!token && (
                <div className="mb-4 p-3 bg-rose-50 border border-rose-200 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-rose-700">
                    {t('pw_reset.token_warning.text')}{' '}
                    <Link to="/passwort-vergessen" className="underline ml-1 font-medium">{t('pw_reset.token_warning.link_text')}</Link>
                    {' '}{t('pw_reset.token_warning.text_suffix')}
                  </div>
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-[10px] tracking-[0.2em] text-[#34536b] mb-1.5">{t('pw_reset.form.new_password_label')}</label>
                  <div className="relative">
                    <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#34536b]/50" />
                    <input
                      type={show ? 'text' : 'password'}
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      required
                      autoFocus
                      minLength={8}
                      data-testid="reset-pw-input"
                      className="w-full pl-8 pr-9 py-2.5 text-sm border border-[#1B3A5C]/20 focus:outline-none focus:border-[#b8893d]"
                    />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#34536b]/50 hover:text-[#1B3A5C]" tabIndex={-1}>
                      {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.2em] text-[#34536b] mb-1.5">{t('pw_reset.form.confirm_password_label')}</label>
                  <div className="relative">
                    <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#34536b]/50" />
                    <input
                      type={show ? 'text' : 'password'}
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      required
                      data-testid="reset-pw-input-2"
                      className="w-full pl-8 pr-3 py-2.5 text-sm border border-[#1B3A5C]/20 focus:outline-none focus:border-[#b8893d]"
                    />
                  </div>
                </div>
                {error && <p className="text-xs text-rose-600">{error}</p>}
                <Button type="submit" disabled={loading || !token} data-testid="reset-pw-submit"
                        className="w-full bg-[#1B3A5C] hover:bg-[#34536b] text-white text-xs tracking-[0.2em] py-2.5">
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('pw_reset.form.submit_button')}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PasswortZurueckSetzen;
